import { useState, useCallback, useEffect } from 'react';
import apiClient, { ApiError } from '../services/apiClient';
import type { Client, ClientFormData } from '../types';

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  // Load all clients from API
  const loadClients = useCallback(async (): Promise<void> => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const clientsData = await apiClient.getClients();
      setClients(clientsData);
      setIsInitialized(true);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to load clients:', apiError);
      
      // Fallback to sample data if API fails during development
      if (apiError.code === 'NETWORK_ERROR' && !isInitialized) {
        console.warn('Using fallback sample data due to API connection issues');
        setClients(SAMPLE_CLIENTS);
        setIsInitialized(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isInitialized]);

  // Create a new client
  const createClient = useCallback(async (clientData: ClientFormData): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const newClient = await apiClient.createClient(clientData);
      
      // Optimistic update
      setClients(prev => [...prev, newClient]);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update an existing client
  const updateClient = useCallback(async (id: string, clientData: ClientFormData): Promise<void> => {
    setIsLoading(true);
    setError(null);

    const originalClients = [...clients];

    try {
      // Optimistic update
      const updatedClient: Client = {
        id,
        name: clientData.name,
        email: clientData.email,
        phone: clientData.phone || undefined,
        company: clientData.company || undefined,
        createdAt: new Date(), // Will be updated with server response
        updatedAt: new Date()
      };

      setClients(prev => prev.map(client => 
        client.id === id ? updatedClient : client
      ));

      // Make API call
      const serverClient = await apiClient.updateClient(id, clientData);
      
      // Update with server response
      setClients(prev => prev.map(client => 
        client.id === id ? serverClient : client
      ));
    } catch (err) {
      // Rollback on error
      setClients(originalClients);
      const apiError = err as ApiError;
      setError(apiError.message);
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  }, [clients]);

  // Delete a client
  const deleteClient = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    const originalClients = [...clients];

    try {
      // Optimistic update
      setClients(prev => prev.filter(client => client.id !== id));

      // Make API call
      await apiClient.deleteClient(id);
    } catch (err) {
      // Rollback on error
      setClients(originalClients);
      const apiError = err as ApiError;
      setError(apiError.message);
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  }, [clients]);

  // Filter clients based on search term
  const filterClients = useCallback((searchTerm: string): Client[] => {
    if (!searchTerm.trim()) return clients;

    const term = searchTerm.toLowerCase();
    return clients.filter(client =>
      client.name?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term) ||
      (client.company && client.company.toLowerCase().includes(term))
    );
  }, [clients]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh clients (for manual refresh)
  const refreshClients = useCallback(() => {
    loadClients();
  }, [loadClients]);

  return {
    clients,
    isLoading,
    error,
    isInitialized,
    createClient,
    updateClient,
    deleteClient,
    filterClients,
    clearError,
    refreshClients
  };
};

// Fallback sample data for development
const SAMPLE_CLIENTS: Client[] = [
  {
    id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '(555) 123-4567',
    company: 'Tech Solutions Inc.',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'b2c3d4e5-f6a7-8901-bcde-f23456789012',
    name: 'Michael Chen',
    email: 'michael.chen@email.com',
    phone: '(555) 987-6543',
    company: 'Creative Agency',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    name: 'Emma Rodriguez',
    email: 'emma.rodriguez@email.com',
    phone: '(555) 456-7890',
    company: 'Marketing Pro',
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'd4e5f6a7-b8c9-0123-defa-456789012345',
    name: 'David Thompson',
    email: 'david.thompson@email.com',
    phone: '(555) 321-0987',
    company: 'Consulting Group',
    createdAt: new Date(),
    updatedAt: new Date()
  }
];
